/* ProfitAndLossHtmlPreview.jsx */
import React, { useState, useEffect, useMemo } from 'react';
import { Button, TextField, IconButton, Popper, Autocomplete, Tooltip, FormControl, InputLabel, Select, MenuItem, FormControlLabel, Switch, Checkbox } from '@mui/material';
import { styled } from '@mui/system';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import AddIcon from '@mui/icons-material/Add';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import Swal from 'sweetalert2';
import ProfitAndLossService from '../../../Services/profitAndLoss.service';
import EnumsService from '../../../Services/enums.service';
import EditEntryModal from './EditEntryModal';

import MatchingReportForm from './MatchingReportForm';
import { wrapHtmlForPreview } from '../../../Utils/profitAndLoss/profitAndLossPreviewUtils';

dayjs.extend(utc);

const StyledPopper = styled(Popper)(({ theme }) => ({
    zIndex: 13000,
    '& .MuiPaper-root': {
        borderRadius: '8px',
        background: '#FFF',
        boxShadow: '0px 4px 8px 0px rgba(0, 0, 0, 0.32)',
    },
    '& .MuiAutocomplete-listbox': {
        padding: theme.spacing(1),
    },
    '& .MuiAutocomplete-option': {
        borderRadius: '4px',
        '&[aria-selected="true"], &.Mui-focused, &:hover': {
            background: '#F1F5FF !important',
        },
    },
}));

const StyledListItem = styled('li')({
    '&:hover': { backgroundColor: '#F5F5F6', cursor: 'pointer' },
});

const ProfitAndLossHtmlPreview = ({
  open = false,
  htmlContent = '',
  onCancel = () => { },
  onOk = () => { },
  t = s => s,
  withCancel = true,
  title = 'ProfitAndLossReport',
  client = null,
  token = '',
  year = null,
  accountantId = '',
  isFromLedgerReport = false, // New prop to indicate if opened from LedgerReport
  profitAndLossTotalBalance = 0 // Total balance from Profit and Loss report (when from LedgerReport)
}) => {
  const [htmlUrl, setHtmlUrl] = useState('');
  const [matchingReports, setMatchingReports] = useState([]);
  const [sortCodes, setSortCodes] = useState([]);
  const [accountCards, setAccountCards] = useState([]); // AccountCards for isFromLedgerReport
  const [totalProfit, setTotalProfit] = useState(0);
  const [loading, setLoading] = useState(false);

  // Form state for adding new entries
  const [addingToAdd, setAddingToAdd] = useState(false); // true when adding to "הוסף" section
  const [addingToSubtract, setAddingToSubtract] = useState(false); // true when adding to "הפחת" section
  const [addingToTaxLiability, setAddingToTaxLiability] = useState(false); // true when adding to "חבות מס" section
  
  // State for inline forms in summary section
  const [addingInSummary, setAddingInSummary] = useState(null); // 'add', 'subtract', 'taxLiability', or null
  
  // State for editing existing entries
  const [editingItemId, setEditingItemId] = useState(null); // ID of item being edited (kept for backward compatibility)
  const [editModalOpen, setEditModalOpen] = useState(false); // Modal open state
  const [editingItem, setEditingItem] = useState(null); // Item being edited in modal
  
  // Form inputs for new entry
  const [keyType, setKeyType] = useState(isFromLedgerReport ? 'accountcard' : 'sortcode'); // 'sortcode' or 'manual' (or 'accountcard' when isFromLedgerReport)
  const [selectedSortCode, setSelectedSortCode] = useState('');
  const [selectedAccountCard, setSelectedAccountCard] = useState(''); // For AccountCard selection when isFromLedgerReport
  const [manualKey, setManualKey] = useState('');
  const [value, setValue] = useState('');
  const [isAmountPercentage, setIsAmountPercentage] = useState(true); // true = Amount, false = Percentage
  
  // State for percentage calculation in inline form
  const [inlineFormSortCodeTotal, setInlineFormSortCodeTotal] = useState(0);
  const [inlineFormPercentage, setInlineFormPercentage] = useState('');
  const [inlineFormCalculatedValue, setInlineFormCalculatedValue] = useState('');
  const [loadingInlineFormSortCodeTotal, setLoadingInlineFormSortCodeTotal] = useState(false);
  
  // Special state for "הכנסה חייבת בשיעור" percentage
  const [taxableIncomePercentage, setTaxableIncomePercentage] = useState('');
  
  // State for deferred tax expenses (הוצאות מס נדחות)
  const [deferredTaxExpenses, setDeferredTaxExpenses] = useState(0);

  // Normalize year: extract year from Date object if needed, otherwise use as-is
  const normalizedYear = useMemo(() => {
    if (!year) return null;
    // If year is a Date object, extract the year using UTC
    if (year instanceof Date) {
      return dayjs.utc(year).year();
    }
    // If year is already a number, use it directly
    if (typeof year === 'number') {
      return year;
    }
    // If year is a string that can be parsed, try to extract year
    if (typeof year === 'string') {
      const parsed = dayjs.utc(year);
      if (parsed.isValid()) {
        return parsed.year();
      }
    }
    return year;
  }, [year]);

  // Input styling for Autocomplete
  const inputPropsStyle = {
    disableUnderline: true,
    className: 'custom-input-box-sizing-toggle',
    sx: {
      height: 36,
      px: '8px',
      borderRadius: 6,
      background: '#F5F5F6',
      border: '1px solid transparent',
      fontSize: '0.75rem',
      '&:focus-within': { border: '2px solid #304FFF', background: '#FFFFFF' },
      '& *': { boxSizing: 'border-box!important' },
    },
  };

  // Prepare sort code options for Autocomplete
  const sortCodeOptions = useMemo(() => {
    return sortCodes.map((sc) => {
      const id = sc.sortCodeId || sc.SortCodeId;
      const name = sc.sortCodeName || sc.SortCodeName;
      return {
        sortCodeId: id,
        sortCodeName: name,
        isEditable: sc.isEditable || false,
      };
    });
  }, [sortCodes]);

  // Prepare account card options for Autocomplete (when isFromLedgerReport)
  const accountCardOptions = useMemo(() => {
    return accountCards.map((ac) => {
      const id = ac.cardId || ac.CardId;
      const name = ac.name || ac.Name;
      return {
        cardId: id,
        name: name,
        isEditable: ac.isEditable || false,
      };
    });
  }, [accountCards]);

  const getKeyDisplay = (report) => {
    // If there's an internalKey, the Key field should already contain the display name
    // But for backward compatibility, check old format
    if (report.isInternalKey) {
      // New format: Key contains the display name, InternalKey contains the ID
      if (report.key) {
        return report.key;
      }
      // Fallback: check if key is in old format "sortCodeId - sortCodeName"
      if (report.key && report.key.includes(' - ')) {
        const parts = report.key.split(' - ');
        if (parts.length > 1) {
          return parts[1]; // Return only the name
        }
      }
      // Fallback: try to find by internalKey (for backward compatibility)
      if (report.internalKey) {
        const sortCode = sortCodes.find(sc => sc.sortCodeId === report.internalKey || sc.SortCodeId === report.internalKey);
        if (sortCode) {
          return sortCode.sortCodeName || sortCode.SortCodeName || report.key;
        }
      }
      return report.key || report.internalKey || '';
    }
    // For manual entries (no internal key), just return the key
    return report.key;
  };

  // Create blob URL from HTML content
  useEffect(() => {
    if (!htmlContent || !open) {
      setHtmlUrl('');
      return;
    }

    const previewHtml = wrapHtmlForPreview(htmlContent);
    const blob = new Blob([previewHtml], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    setHtmlUrl(url);

    return () => {
      if (url) {
        URL.revokeObjectURL(url);
      }
    };
  }, [htmlContent, open]);

  // Update keyType when isFromLedgerReport changes
  useEffect(() => {
    if (isFromLedgerReport && keyType === 'sortcode') {
      setKeyType('accountcard');
    } else if (!isFromLedgerReport && keyType === 'accountcard') {
      setKeyType('sortcode');
    }
  }, [isFromLedgerReport, keyType]);

  // Load data when modal opens
  useEffect(() => {
    if (open && client && token && normalizedYear) {
      loadMatchingReports();
      if (isFromLedgerReport) {
        loadAccountCards();
      } else {
        loadSortCodes();
      }
      loadTotalProfit();
    }
  }, [open, client, token, normalizedYear, isFromLedgerReport]);

  // Fetch SortCode or AccountCard total for inline form when selected
  useEffect(() => {
    const fetchInlineFormTotal = async () => {
      // Fetch for SortCode when not from LedgerReport
      if (keyType === 'sortcode' && selectedSortCode && client && normalizedYear && token && !isFromLedgerReport) {
        setLoadingInlineFormSortCodeTotal(true);
        try {
          const data = await getSortCodeTotal(client.caseId, client.accountantId, normalizedYear, selectedSortCode, token);
          if (data && data.sortCodeTotal !== undefined) {
            const total = data.sortCodeTotal || 0;
            setInlineFormSortCodeTotal(total);
            // When SortCode is selected, set the total based on mode
            if (isAmountPercentage) {
              // Amount mode: set the total as the value
              setValue(total.toFixed(2));
            } else {
              // Percentage mode: set the total as the initial calculated value
              setInlineFormCalculatedValue(total.toFixed(2));
              setValue(total.toFixed(2));
            }
            console.log('Inline form SortCode total fetched:', total, 'for SortCode:', selectedSortCode);
          }
        } catch (error) {
          console.error('Error fetching sort code total for inline form:', error);
          setInlineFormSortCodeTotal(0);
          setInlineFormCalculatedValue('');
          if (isAmountPercentage) {
            setValue('');
          }
        } finally {
          setLoadingInlineFormSortCodeTotal(false);
        }
      }
      // Fetch for AccountCard when from LedgerReport
      else if (keyType === 'accountcard' && selectedAccountCard && client && normalizedYear && token && isFromLedgerReport) {
        setLoadingInlineFormSortCodeTotal(true);
        try {
          const data = await getAccountCardTotal(client.caseId, client.accountantId, normalizedYear, selectedAccountCard, token);
          if (data && data.sortCodeTotal !== undefined) {
            const total = data.sortCodeTotal || 0;
            setInlineFormSortCodeTotal(total);
            // When AccountCard is selected, set the total based on mode
            if (isAmountPercentage) {
              // Amount mode: set the total as the value
              setValue(total.toFixed(2));
            } else {
              // Percentage mode: set the total as the initial calculated value
              setInlineFormCalculatedValue(total.toFixed(2));
              setValue(total.toFixed(2));
            }
            console.log('Inline form AccountCard total fetched:', total, 'for AccountCard:', selectedAccountCard);
          }
        } catch (error) {
          console.error('Error fetching account card total for inline form:', error);
          setInlineFormSortCodeTotal(0);
          setInlineFormCalculatedValue('');
          if (isAmountPercentage) {
            setValue('');
          }
        } finally {
          setLoadingInlineFormSortCodeTotal(false);
        }
      } else {
        setInlineFormSortCodeTotal(0);
        if (!isAmountPercentage) {
          setInlineFormCalculatedValue('');
        }
        if (isAmountPercentage && (keyType === 'accountcard' || keyType === 'sortcode')) {
          setValue('');
        }
      }
    };

    fetchInlineFormTotal();
  }, [keyType, selectedSortCode, selectedAccountCard, client, normalizedYear, token, isAmountPercentage, isFromLedgerReport]);

  // Calculate value from percentage for inline form
  useEffect(() => {
    // Allow negative totals - change condition from > 0 to !== 0
    if (!isAmountPercentage && inlineFormSortCodeTotal !== 0) {
      if (inlineFormPercentage && inlineFormPercentage.trim() !== '') {
        const percentageNum = parseFloat(inlineFormPercentage);
        if (!isNaN(percentageNum) && percentageNum > 0) {
          // Calculate: percentage × total (works with both positive and negative totals)
          const calculated = (percentageNum / 100) * inlineFormSortCodeTotal;
          const calculatedStr = calculated.toFixed(2);
          setInlineFormCalculatedValue(calculatedStr);
          setValue(calculatedStr);
          console.log('Inline form calculated value:', calculatedStr, 'from percentage:', percentageNum, '% of total:', inlineFormSortCodeTotal);
        }
      } else {
        // When percentage is cleared/empty, reset calculated value to the original total
        const totalStr = inlineFormSortCodeTotal.toFixed(2);
        setInlineFormCalculatedValue(totalStr);
        setValue(totalStr);
        console.log('Inline form: percentage cleared, resetting to total:', totalStr);
      }
    } else if (isAmountPercentage) {
      // Don't clear percentage when switching to amount mode - preserve it
      // Only clear calculated value
      setInlineFormCalculatedValue('');
    }
  }, [inlineFormPercentage, inlineFormSortCodeTotal, isAmountPercentage]);

  // Calculate derived values using useMemo to avoid recalculating on every render
  const addItems = useMemo(() => matchingReports.filter(r => r.type === 0), [matchingReports]);
  const subtractItems = useMemo(() => matchingReports.filter(r => r.type === 1), [matchingReports]);
  const taxLiabilityItems = useMemo(() => matchingReports.filter(r => r.type === 2 && r.key !== 'הוצאות מס נדחות'), [matchingReports]);
  
  // Find the special "הכנסה חייבת בשיעור" entry in הפחת
  const taxableIncomeRateItem = useMemo(() => 
    subtractItems.find(item => item.key === 'הכנסה חייבת בשיעור'), 
    [subtractItems]
  );
  
  // Calculate taxable income base (sum of הוסף - sum of הפחת excluding the special rate item)
  // When from LedgerReport, use profitAndLossTotalBalance instead of totalProfit
  const taxableIncomeBase = useMemo(() => {
    const baseProfit = isFromLedgerReport ? profitAndLossTotalBalance : totalProfit;
    return baseProfit + 
      addItems.reduce((sum, item) => sum + (parseFloat(item.value) || 0), 0) - 
      subtractItems.filter(item => item.key !== 'הכנסה חייבת בשיעור').reduce((sum, item) => sum + (parseFloat(item.value) || 0), 0);
  }, [totalProfit, profitAndLossTotalBalance, isFromLedgerReport, addItems, subtractItems]);
  
  // Calculate tax amount if percentage is set
  const taxPercentage = useMemo(() => 
    taxableIncomeRateItem ? parseFloat(taxableIncomeRateItem.value) : parseFloat(taxableIncomePercentage) || 0,
    [taxableIncomeRateItem, taxableIncomePercentage]
  );
  
  const calculatedTaxAmount = useMemo(() => 
    taxPercentage > 0 ? (taxableIncomeBase * taxPercentage / 100) : 0,
    [taxPercentage, taxableIncomeBase]
  );
  
  // Update or add corporate tax item when percentage changes
  useEffect(() => {
    if (!open || !client || !year) return;
    
    if (taxPercentage > 0 && calculatedTaxAmount > 0) {
      setMatchingReports(prev => {
        const existingCorporateTax = prev.find(r => r.key === 'בגין מס חברות' && r.type === 2);
        const calculatedValue = calculatedTaxAmount.toFixed(2);
        
        if (existingCorporateTax) {
          // Only update if the value has changed (to avoid unnecessary updates and mark as modified)
          const existingValue = parseFloat(existingCorporateTax.value) || 0;
          const newValue = parseFloat(calculatedValue) || 0;
          
          if (Math.abs(existingValue - newValue) > 0.01) {
            // Value changed, update and mark as modified
            return prev.map(r => 
              r.id === existingCorporateTax.id 
                ? { 
                    ...r, 
                    value: calculatedValue, 
                    isAmountPercentage: true,
                    isModified: !r.id.toString().startsWith('temp_') // Mark as modified if it's not a new entry
                  }
                : r
            );
          }
          // Value hasn't changed, no need to update
          return prev;
        } else {
          // Check if it already exists (avoid duplicates)
          if (prev.find(r => r.key === 'בגין מס חברות' && r.type === 2)) {
            return prev;
          }
          // Add new
          const newCorporateTax = {
            id: `temp_corporate_tax_${Date.now()}`,
            caseId: client.caseId,
            key: 'בגין מס חברות',
            isInternalKey: false,
            internalKey: null,
            type: 2, // TaxLiability
            value: calculatedValue,
            isAmountPercentage: true,
            year: normalizedYear,
            isFromProfitAndLossDetailed: isFromLedgerReport,
            isNew: true
          };
          return [...prev, newCorporateTax];
        }
      });
    } else {
      // Remove corporate tax if percentage is 0 or not set (only temp ones)
      setMatchingReports(prev => prev.filter(r => !(r.key === 'בגין מס חברות' && r.type === 2 && r.id.toString().startsWith('temp_'))));
    }
  }, [taxPercentage, calculatedTaxAmount, open, client, year, isFromLedgerReport]);

  const loadMatchingReports = async () => {
    try {
      // Filter by isFromProfitAndLossDetailed:
      // - If opened from LedgerReport (Profit and Loss Detailed), filter by isFromProfitAndLossDetailed = true
      // - If opened from regular ProfitAndLossReport, filter by isFromProfitAndLossDetailed = false
      const isFromProfitAndLossDetailed = isFromLedgerReport ? true : false;
      const data = await ProfitAndLossService.GetMatchingProfitAndLossReports(
        client.caseId,
        client.accountantId,
        dayjs.utc(`${normalizedYear}-01-01`).toDate(),
        dayjs.utc(`${normalizedYear}-12-31`).toDate(),
        token,
        isFromProfitAndLossDetailed,
        true
      );
      
      setMatchingReports(data || []);
      
      // Set the taxable income percentage if it exists
      const taxableIncomeRate = data?.find(r => r.key === 'הכנסה חייבת בשיעור' && r.type === 1);
      if (taxableIncomeRate) {
        setTaxableIncomePercentage(taxableIncomeRate.value);
      }
      
      // Set the deferred tax expenses if it exists
      const deferredTax = data?.find(r => r.key === 'הוצאות מס נדחות' && r.type === 2);
      if (deferredTax) {
        setDeferredTaxExpenses(parseFloat(deferredTax.value) || 0);
      } else {
        setDeferredTaxExpenses(0);
      }
    } catch (error) {
      console.error('Error loading matching reports:', error);
    }
  };

  const loadSortCodes = async () => {
    try {
      const response = await EnumsService.GetSortCodesByAccountantId(accountantId, token);
      
      if (response.ok) {
        const data = await response.json();
        setSortCodes(data || []);
      }
    } catch (error) {
      console.error('Error loading sort codes:', error);
    }
  };

  const loadAccountCards = async () => {
    try {
      const response = await EnumsService.GetAccountCardsByCaseId(client.caseId, token);
      
      if (response.ok) {
        const data = await response.json();
        setAccountCards(data || []);
      }
    } catch (error) {
      console.error('Error loading account cards:', error);
    }
  };

  const loadTotalProfit = async () => {
    try {
      const data = await ProfitAndLossService.GetYearlyProfitTotal(
        client.caseId,
        client.accountantId,
        dayjs.utc(`${normalizedYear}-01-01`).toDate(),
        dayjs.utc(`${normalizedYear}-12-31`).toDate(),
        token,
        null,
        true,
      );
      setTotalProfit(data.totalProfit || 0);
    } catch (error) {
      console.error('Error loading total profit:', error);
    }
  };

  // Function to get SortCode or AccountCard total (passed to AddEntryForm and EditEntryModal)
  const getSortCodeTotal = async (caseId, accountantId, year, sortCodeId, token) => {
    try {
      const startDate = dayjs.utc(`${normalizedYear}-01-01`).toDate();
      const endDate = dayjs.utc(`${normalizedYear}-12-31`).toDate();
      const data = await ProfitAndLossService.GetSortCodeTotalByYear(
        caseId,
        accountantId,
        startDate,
        endDate,
        sortCodeId,
        token
      );
      return data;
    } catch (error) {
      console.error('Error getting sort code total:', error);
      throw error;
    }
  };

  // Function to get AccountCard total (for isFromLedgerReport)
  const getAccountCardTotal = async (caseId, accountantId, year, accountCardId, token) => {
    try {
      const startDate = dayjs.utc(`${normalizedYear}-01-01`).toDate();
      const endDate = dayjs.utc(`${normalizedYear}-12-31`).toDate();
      const data = await ProfitAndLossService.GetAccountCardTotalByYear(
        caseId,
        accountantId,
        startDate,
        endDate,
        accountCardId,
        token
      );
      return data;
    } catch (error) {
      console.error('Error getting account card total:', error);
      throw error;
    }
  };

  const handleAddEntry = async (type, percentageFromForm = null, saveImmediately = false) => {
    // For percentage mode, check if percentage is set instead of value
    // If percentageFromForm is provided (from inline form), use it; otherwise check value
    const useAccountCard = isFromLedgerReport && keyType === 'accountcard';
    const selectedKey = useAccountCard ? selectedAccountCard : selectedSortCode;
    
    const hasValue = isAmountPercentage 
      ? value 
      : (percentageFromForm !== null ? inlineFormPercentage : (value && parseFloat(value) > 0));
    if (!hasValue || (!selectedKey && !manualKey)) return;

    let key;
    let internalKey = null;
    let isInternalKey = false;
    // Use percentage from form if provided (from AddEntryForm component), 
    // otherwise use inlineFormPercentage if in percentage mode (from inline form)
    let percentageValue = percentageFromForm !== null 
      ? percentageFromForm 
      : (!isAmountPercentage && inlineFormPercentage ? parseFloat(inlineFormPercentage) : null);
    
    // For inline form, use the calculated value if in percentage mode
    let finalValue = (!isAmountPercentage && inlineFormCalculatedValue) ? inlineFormCalculatedValue : value;
    
    // For הפחת (Subtract) items (type === 1), if value is negative, convert to positive
    // This ensures that negative sort code totals are saved as positive values to be subtracted
    if (type === 1 && finalValue) {
      const valueNum = parseFloat(finalValue);
      if (!isNaN(valueNum) && valueNum < 0) {
        // Convert negative to positive for subtract items
        finalValue = Math.abs(valueNum).toString();
        console.log('Converted negative value to positive for subtract item:', valueNum, '->', finalValue);
      }
    }

    if (keyType === 'sortcode' || (isFromLedgerReport && keyType === 'accountcard')) {
      if (useAccountCard) {
        // Find the selected account card option to get both ID and name
        const selectedOption = accountCardOptions.find(opt => opt.cardId === selectedAccountCard);
        if (selectedOption) {
          internalKey = selectedOption.cardId; // Save CardId in InternalKey
          key = selectedOption.name; // Save Name in Key
          isInternalKey = true;
        } else {
          // Fallback: use selectedAccountCard as internalKey and key
          internalKey = selectedAccountCard;
          key = selectedAccountCard;
          isInternalKey = true;
        }
      } else {
        // Find the selected sort code option to get both ID and name
        const selectedOption = sortCodeOptions.find(opt => opt.sortCodeId === selectedSortCode);
        if (selectedOption) {
          internalKey = selectedOption.sortCodeId; // Save SortCodeId in InternalKey
          key = selectedOption.sortCodeName; // Save SortCodeName in Key
          isInternalKey = true;
        } else {
          // Fallback: use selectedSortCode as internalKey and key
          internalKey = selectedSortCode;
          key = selectedSortCode;
          isInternalKey = true;
        }
      }
    } else {
      // Manual key - check if it's an AccountCard format (cardId - cardName) or truly manual
      if (manualKey && manualKey.includes(' - ')) {
        // Format: "cardId - cardName" - treat as AccountCard
        const parts = manualKey.split(' - ');
        if (parts.length > 1) {
          internalKey = parts[0]; // Save CardId in InternalKey
          key = parts[1]; // Save CardName in Key
          isInternalKey = true;
        } else {
          key = manualKey;
          isInternalKey = false;
        }
      } else {
        // Truly manual text - no internal key
        key = manualKey;
        internalKey = null;
        isInternalKey = false;
      }
    }

    const newReport = {
      id: `temp_${Date.now()}_${Math.random()}`, // Temporary ID for local state
      caseId: client.caseId,
      key: key,
      isInternalKey: isInternalKey,
      internalKey: internalKey,
      type: type, // 0 = Add, 1 = Subtract, 2 = TaxLiability
      value: finalValue, // Use calculated value if in percentage mode, otherwise use value
      isAmountPercentage: isAmountPercentage,
      percentage: percentageValue, // Will be set by AddEntryForm or inline form when in percentage mode
      year: year,
      isFromProfitAndLossDetailed: isFromLedgerReport, // Set based on where modal was opened from
      isNew: true // Flag to indicate this is a new entry not yet saved
    };

    // Add to local state immediately
    setMatchingReports(prev => [...prev, newReport]);
    
    // If saveImmediately is true, save to database right away
    if (saveImmediately) {
      setLoading(true);
      try {
        const entryToSave = {
          id: '00000000-0000-0000-0000-000000000000', // Empty GUID for new entries
          caseId: newReport.caseId,
          key: newReport.key,
          isInternalKey: newReport.isInternalKey ?? false,
          internalKey: (newReport.isInternalKey && newReport.internalKey) ? newReport.internalKey : null,
          type: newReport.type,
          value: newReport.value,
          isAmountPercentage: newReport.isAmountPercentage,
          percentage: newReport.percentage ?? null,
          year: newReport.year,
          isFromProfitAndLossDetailed: newReport.isFromProfitAndLossDetailed ?? isFromLedgerReport
        };

        const savedReports = await ProfitAndLossService.SaveMatchingProfitAndLossReports([entryToSave], token);
        
        if (savedReports && savedReports.length > 0) {
          // Replace temporary entry with saved one
          setMatchingReports(prev => prev.map(r => 
            r.id === newReport.id 
              ? { ...savedReports[0], isNew: false, isModified: false }
              : r
          ));
          
          // After saving the entry, recalculate and save corporate tax if needed
          // Get current state to calculate new taxable income base
          setMatchingReports(prev => {
            const updated = prev.map(r => 
              r.id === newReport.id 
                ? { ...savedReports[0], isNew: false, isModified: false }
                : r
            );
            
            // Calculate new taxableIncomeBase with the new entry included
            const baseProfit = isFromLedgerReport ? profitAndLossTotalBalance : totalProfit;
            const updatedAddItems = updated.filter(r => r.type === 0);
            const updatedSubtractItems = updated.filter(r => r.type === 1 && r.key !== 'הכנסה חייבת בשיעור');
            
            const newTaxableIncomeBase = baseProfit + 
              updatedAddItems.reduce((sum, item) => sum + (parseFloat(item.value) || 0), 0) - 
              updatedSubtractItems.reduce((sum, item) => sum + (parseFloat(item.value) || 0), 0);
            
            // Calculate new corporate tax if percentage is set
            const currentTaxPercentage = taxPercentage;
            if (currentTaxPercentage > 0 && newTaxableIncomeBase > 0) {
              const newCalculatedTaxAmount = (newTaxableIncomeBase * currentTaxPercentage / 100);
              const existingCorporateTax = updated.find(r => r.key === 'בגין מס חברות' && r.type === 2);
              
              if (existingCorporateTax) {
                const newValue = newCalculatedTaxAmount.toFixed(2);
                // Update corporate tax in state first
                const updatedWithCorporateTax = updated.map(r => 
                  r.id === existingCorporateTax.id 
                    ? { ...r, value: newValue, isModified: !r.id.toString().startsWith('temp_') }
                    : r
                );
                
                // Save corporate tax to database asynchronously (don't await to avoid blocking)
                const corporateTaxToSave = {
                  id: existingCorporateTax.id.toString().startsWith('temp_') 
                    ? '00000000-0000-0000-0000-000000000000' 
                    : existingCorporateTax.id,
                  caseId: existingCorporateTax.caseId || client.caseId,
                  key: 'בגין מס חברות',
                  isInternalKey: false,
                  internalKey: null,
                  type: 2, // TaxLiability
                  value: newValue,
                  isAmountPercentage: true,
                  year: existingCorporateTax.year || year,
                  isFromProfitAndLossDetailed: existingCorporateTax.isFromProfitAndLossDetailed ?? isFromLedgerReport
                };
                
                // Save corporate tax to database
                ProfitAndLossService.SaveMatchingProfitAndLossReports([corporateTaxToSave], token)
                  .then(savedCorporateTaxReports => {
                    if (savedCorporateTaxReports && savedCorporateTaxReports.length > 0) {
                      // Update corporate tax in state with saved version
                      setMatchingReports(prevState => prevState.map(r => 
                        r.id === existingCorporateTax.id 
                          ? { ...savedCorporateTaxReports[0], isNew: false, isModified: false }
                          : r
                      ));
                    }
                  })
                  .catch(error => {
                    console.error('Error saving corporate tax:', error);
                  });
                
                return updatedWithCorporateTax;
              }
            }
            
            return updated;
          });
          
          toast.success(t('EntrySavedSuccessfully'));
        }
      } catch (error) {
        console.error('Error saving entry:', error);
        toast.error(t('ErrorSavingEntry') + (error.message ? `: ${error.message}` : ''));
      } finally {
        setLoading(false);
      }
    }
    
    // Reset form inputs but keep the form open for adding another entry
    setSelectedSortCode('');
    setSelectedAccountCard('');
    setManualKey('');
    setValue('');
    setKeyType(isFromLedgerReport ? 'accountcard' : 'sortcode');
    setInlineFormSortCodeTotal(0);
    setInlineFormPercentage('');
    setInlineFormCalculatedValue('');
    // Don't reset addingToAdd/addingToSubtract/addingToTaxLiability/addingInSummary - keep form open
  };

  const handleCancelAdd = () => {
    setSelectedSortCode('');
    setSelectedAccountCard('');
    setManualKey('');
    setValue('');
    setAddingToAdd(false);
    setAddingToSubtract(false);
    setAddingToTaxLiability(false);
    setAddingInSummary(null);
    setEditingItemId(null);
    setKeyType(isFromLedgerReport ? 'accountcard' : 'sortcode');
    setInlineFormSortCodeTotal(0);
    setInlineFormPercentage('');
    setInlineFormCalculatedValue('');
  };

  // Handler to save taxable income percentage entry immediately
  const handleSaveTaxableIncomePercentage = async (percentage) => {
    setLoading(true);
    try {
      const existingItem = matchingReports.find(r => r.key === 'הכנסה חייבת בשיעור' && r.type === 1);
      const existingCorporateTax = matchingReports.find(r => r.key === 'בגין מס חברות' && r.type === 2);
      const existingDeferredTax = matchingReports.find(r => r.key === 'הוצאות מס נדחות' && r.type === 2);
      
      // Calculate the new corporate tax amount based on the new percentage
      const percentageNum = parseFloat(percentage) || 0;
      const newCalculatedTaxAmount = percentageNum > 0 && taxableIncomeBase > 0 
        ? (taxableIncomeBase * percentageNum / 100) 
        : 0;
      
      // Prepare entries to save
      const entriesToSave = [];
      
      // Add taxable income percentage entry
      entriesToSave.push({
        id: existingItem ? existingItem.id : '00000000-0000-0000-0000-000000000000', // Empty GUID for new entries
        caseId: client.caseId,
        key: 'הכנסה חייבת בשיעור',
        isInternalKey: false,
        internalKey: null,
        type: 1, // Subtract
        value: percentage,
        isAmountPercentage: false, // This is a percentage
        percentage: percentageNum || null,
        year: year,
        isFromProfitAndLossDetailed: isFromLedgerReport
      });
      
      // If percentage is 0, delete corporate tax and deferred tax entries
      if (percentageNum === 0) {
        // Delete corporate tax entry if it exists
        if (existingCorporateTax && !existingCorporateTax.id.toString().startsWith('temp_')) {
          try {
            await ProfitAndLossService.DeleteMatchingProfitAndLossReport(existingCorporateTax.id, token);
          } catch (error) {
            console.error('Error deleting corporate tax entry:', error);
          }
        }
        
        // Delete deferred tax entry if it exists and has a value
        if (existingDeferredTax && !existingDeferredTax.id.toString().startsWith('temp_')) {
          const deferredTaxValue = parseFloat(existingDeferredTax.value) || 0;
          if (deferredTaxValue > 0) {
            try {
              await ProfitAndLossService.DeleteMatchingProfitAndLossReport(existingDeferredTax.id, token);
            } catch (error) {
              console.error('Error deleting deferred tax entry:', error);
            }
          }
        }
        
        // Delete all other tax liability items (except deferred tax if value is 0)
        const otherTaxLiabilityItems = matchingReports.filter(r => 
          r.type === 2 && 
          r.key !== 'בגין מס חברות' && 
          r.key !== 'הוצאות מס נדחות' &&
          !r.id.toString().startsWith('temp_')
        );
        
        for (const item of otherTaxLiabilityItems) {
          try {
            await ProfitAndLossService.DeleteMatchingProfitAndLossReport(item.id, token);
          } catch (error) {
            console.error(`Error deleting tax liability item ${item.key}:`, error);
          }
        }
      } else {
        // If percentage > 0, save corporate tax entry
        if (existingCorporateTax || newCalculatedTaxAmount > 0) {
          entriesToSave.push({
            id: existingCorporateTax && !existingCorporateTax.id.toString().startsWith('temp_')
              ? existingCorporateTax.id
              : '00000000-0000-0000-0000-000000000000', // Empty GUID for new entries
            caseId: client.caseId,
            key: 'בגין מס חברות',
            isInternalKey: false,
            internalKey: null,
            type: 2, // TaxLiability
            value: newCalculatedTaxAmount.toFixed(2),
            isAmountPercentage: true,
            percentage: null,
            year: normalizedYear,
            isFromProfitAndLossDetailed: isFromLedgerReport
          });
        }
      }

      // Save entries (only if there are entries to save)
      if (entriesToSave.length > 0) {
        const savedReports = await ProfitAndLossService.SaveMatchingProfitAndLossReports(entriesToSave, token);
        
        if (savedReports && savedReports.length > 0) {
          // Update local state with saved reports
          setMatchingReports(prev => {
            let updated = [...prev];
            
            // Update taxable income percentage entry
            const savedTaxableIncome = savedReports.find(r => r.key === 'הכנסה חייבת בשיעור' && r.type === 1);
            if (savedTaxableIncome) {
              if (existingItem) {
                updated = updated.map(r => 
                  r.id === existingItem.id 
                    ? { ...savedTaxableIncome, isNew: false, isModified: false }
                    : r
                );
              } else {
                updated.push({ ...savedTaxableIncome, isNew: false, isModified: false });
              }
            }
            
            // Update corporate tax entry if percentage > 0
            if (percentageNum > 0) {
              const savedCorporateTax = savedReports.find(r => r.key === 'בגין מס חברות' && r.type === 2);
              if (savedCorporateTax) {
                const existingCorporateTaxInState = updated.find(r => r.key === 'בגין מס חברות' && r.type === 2);
                if (existingCorporateTaxInState) {
                  updated = updated.map(r => 
                    r.id === existingCorporateTaxInState.id 
                      ? { ...savedCorporateTax, isNew: false, isModified: false }
                      : r
                  );
                } else {
                  updated.push({ ...savedCorporateTax, isNew: false, isModified: false });
                }
              }
            }
            
            return updated;
          });
        }
      }
      
      // If percentage is 0, remove entries from local state
      if (percentageNum === 0) {
        setMatchingReports(prev => {
          let updated = prev.filter(r => {
            // Remove corporate tax
            if (r.key === 'בגין מס חברות' && r.type === 2) {
              return false;
            }
            // Remove deferred tax if it had a value
            if (r.key === 'הוצאות מס נדחות' && r.type === 2) {
              const value = parseFloat(r.value) || 0;
              if (value > 0 && !r.id.toString().startsWith('temp_')) {
                return false;
              }
            }
            // Remove all other tax liability items (except deferred tax with 0 value)
            if (r.type === 2 && 
                r.key !== 'הוצאות מס נדחות' && 
                !r.id.toString().startsWith('temp_')) {
              return false;
            }
            return true;
          });
          return updated;
        });
      }
      
      // Update the taxable income percentage state to trigger UI recalculation
      setTaxableIncomePercentage(percentage);
      
      // Reload matching reports to ensure consistency
      await loadMatchingReports();
      
      toast.success(t('EntrySavedSuccessfully') || 'Entry saved successfully.');
    } catch (error) {
      console.error('Error saving taxable income percentage:', error);
      toast.error(t('ErrorSavingEntry') + (error.message ? `: ${error.message}` : ''));
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (item) => {
    // Set the item to edit and open modal
    setEditingItem(item);
    setEditModalOpen(true);
    setAddingToAdd(false);
    setAddingToSubtract(false);
    setAddingToTaxLiability(false);
    setAddingInSummary(null);
    
    // Populate form with item data
    if (item.isInternalKey && item.internalKey) {
      if (isFromLedgerReport) {
        // When from LedgerReport, check if it's an AccountCard
        const accountCardOption = accountCardOptions.find(opt => opt.cardId === item.internalKey);
        if (accountCardOption) {
          setKeyType('accountcard');
          setSelectedAccountCard(item.internalKey);
          setSelectedSortCode('');
          setManualKey('');
        } else {
          // It's not found in AccountCards - format as "cardId - cardName"
          setKeyType('manual');
          setManualKey(`${item.internalKey} - ${item.key}`);
          setSelectedAccountCard('');
          setSelectedSortCode('');
        }
      } else {
        // Check if it's a sort code by trying to find it in sortCodeOptions
        const sortCodeOption = sortCodeOptions.find(opt => opt.sortCodeId === item.internalKey);
        if (sortCodeOption) {
          setKeyType('sortcode');
          setSelectedSortCode(item.internalKey);
          setSelectedAccountCard('');
          setManualKey('');
        } else {
          // It's an AccountCard - format as "cardId - cardName"
          setKeyType('manual');
          setManualKey(`${item.internalKey} - ${item.key}`);
          setSelectedSortCode('');
          setSelectedAccountCard('');
        }
      }
    } else {
      // Manual text (no internal key) or backward compatibility
      setKeyType('manual');
      // For backward compatibility, check if key is in old format "sortCodeId - sortCodeName" or "cardId - cardName"
      if (item.key && item.key.includes(' - ')) {
        setManualKey(item.key);
      } else {
        setManualKey(item.key || '');
      }
      setSelectedSortCode('');
      setSelectedAccountCard('');
    }
    setValue(item.value);
    setIsAmountPercentage(item.isAmountPercentage);
  };

  const handleCloseEditModal = () => {
    setEditModalOpen(false);
    setEditingItem(null);
    handleCancelAdd();
  };
  
  // Helper function to update corporate tax after add/delete/update operations
  const updateCorporateTaxAfterChange = (updatedReports) => {
    // Calculate new taxableIncomeBase with updated entries
    const baseProfit = isFromLedgerReport ? profitAndLossTotalBalance : totalProfit;
    const updatedAddItems = updatedReports.filter(r => r.type === 0);
    const updatedSubtractItems = updatedReports.filter(r => r.type === 1 && r.key !== 'הכנסה חייבת בשיעור');
    
    const newTaxableIncomeBase = baseProfit + 
      updatedAddItems.reduce((sum, item) => sum + (parseFloat(item.value) || 0), 0) - 
      updatedSubtractItems.reduce((sum, item) => sum + (parseFloat(item.value) || 0), 0);
    
    // Calculate new corporate tax if percentage is set
    const currentTaxPercentage = taxPercentage;
    if (currentTaxPercentage > 0 && newTaxableIncomeBase > 0) {
      const newCalculatedTaxAmount = (newTaxableIncomeBase * currentTaxPercentage / 100);
      const existingCorporateTax = updatedReports.find(r => r.key === 'בגין מס חברות' && r.type === 2);
      
      if (existingCorporateTax) {
        const newValue = newCalculatedTaxAmount.toFixed(2);
        
        // Update corporate tax in state
        setMatchingReports(prevState => prevState.map(r => 
          r.id === existingCorporateTax.id 
            ? { ...r, value: newValue, isModified: !r.id.toString().startsWith('temp_') }
            : r
        ));
        
        // Save corporate tax to database
        const corporateTaxToSave = {
          id: existingCorporateTax.id.toString().startsWith('temp_') 
            ? '00000000-0000-0000-0000-000000000000' 
            : existingCorporateTax.id,
          caseId: existingCorporateTax.caseId || client.caseId,
          key: 'בגין מס חברות',
          isInternalKey: false,
          internalKey: null,
          type: 2, // TaxLiability
          value: newValue,
          isAmountPercentage: true,
          year: existingCorporateTax.year || year,
          isFromProfitAndLossDetailed: existingCorporateTax.isFromProfitAndLossDetailed ?? isFromLedgerReport
        };
        
        ProfitAndLossService.SaveMatchingProfitAndLossReports([corporateTaxToSave], token)
          .then(savedCorporateTaxReports => {
            if (savedCorporateTaxReports && savedCorporateTaxReports.length > 0) {
              setMatchingReports(prevState => prevState.map(r => 
                r.id === existingCorporateTax.id 
                  ? { ...savedCorporateTaxReports[0], isNew: false, isModified: false }
                  : r
              ));
            }
          })
          .catch(error => {
            console.error('Error saving corporate tax:', error);
          });
      }
    }
  };

  const handleUpdateEntry = async (itemId = null, percentageFromModal = null, calculatedValueFromModal = null) => {
    const targetId = itemId || (editingItem ? editingItem.id : null);
    // For percentage mode, use calculated value from modal if provided, otherwise use value
    const finalValueToUse = (!isAmountPercentage && calculatedValueFromModal) 
      ? calculatedValueFromModal 
      : value;
    // For percentage mode, check if percentage is set instead of value
    const useAccountCard = isFromLedgerReport && keyType === 'accountcard';
    const selectedKey = useAccountCard ? selectedAccountCard : selectedSortCode;
    
    const hasValue = isAmountPercentage 
      ? value 
      : (percentageFromModal !== null || calculatedValueFromModal ? true : (value && parseFloat(value) > 0));
    
    // Validation: check if we have a valid key selection
    const hasValidKey = (keyType === 'sortcode' && selectedSortCode) ||
                       (keyType === 'accountcard' && isFromLedgerReport && selectedAccountCard) ||
                       (keyType === 'manual' && manualKey);
    
    if (!targetId || !hasValue || !hasValidKey) return;

    // Show confirmation dialog
    const result = await Swal.fire({
      title: t('ConfirmUpdate') || 'Confirm Update',
      text: t('AreYouSureUpdateEntry') || 'Are you sure you want to update this entry?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#304FFF',
      cancelButtonColor: '#6c757d',
      confirmButtonText: t('YesUpdate') || 'Yes, Update',
      cancelButtonText: t('Cancel') || 'Cancel',
      reverseButtons: true
    });

    if (!result.isConfirmed) {
      return;
    }

    let key;
    let internalKey = null;
    let isInternalKey = false;

    if (keyType === 'sortcode' || (isFromLedgerReport && keyType === 'accountcard')) {
      if (isFromLedgerReport && keyType === 'accountcard') {
        // Find the selected account card option to get both ID and name
        const selectedOption = accountCardOptions.find(opt => opt.cardId === selectedAccountCard);
        if (selectedOption) {
          internalKey = selectedOption.cardId; // Save CardId in InternalKey
          key = selectedOption.name; // Save Name in Key
          isInternalKey = true;
        } else {
          // Fallback: use selectedAccountCard as internalKey and key
          internalKey = selectedAccountCard;
          key = selectedAccountCard;
          isInternalKey = true;
        }
      } else {
        // Find the selected sort code option to get both ID and name
        const selectedOption = sortCodeOptions.find(opt => opt.sortCodeId === selectedSortCode);
        if (selectedOption) {
          internalKey = selectedOption.sortCodeId; // Save SortCodeId in InternalKey
          key = selectedOption.sortCodeName; // Save SortCodeName in Key
          isInternalKey = true;
        } else {
          // Fallback: use selectedSortCode as internalKey and key
          internalKey = selectedSortCode;
          key = selectedSortCode;
          isInternalKey = true;
        }
      }
    } else {
      // Manual key - check if it's an AccountCard format (cardId - cardName) or truly manual
      if (manualKey && manualKey.includes(' - ')) {
        // Format: "cardId - cardName" - treat as AccountCard
        const parts = manualKey.split(' - ');
        if (parts.length > 1) {
          internalKey = parts[0]; // Save CardId in InternalKey
          key = parts[1]; // Save CardName in Key
          isInternalKey = true;
        } else {
          key = manualKey;
          isInternalKey = false;
        }
      } else {
        // Truly manual text - no internal key
        key = manualKey;
        internalKey = null;
        isInternalKey = false;
      }
    }

    const isNewEntry = targetId.toString().startsWith('temp_');
    
    // Use percentage from modal if provided, otherwise use existing percentage
    // IMPORTANT: If switching to amount mode (isAmountPercentage = true), always clear percentage
    let percentageValue = isAmountPercentage 
      ? null 
      : (percentageFromModal !== null 
          ? percentageFromModal 
          : (editingItem?.percentage ?? null));
    
    // For percentage mode, use calculated value from modal if provided, otherwise use value state
    // This ensures we use the correct calculated value (44431.19) not the total (222155.93)
    const finalValue = (!isAmountPercentage && calculatedValueFromModal) 
      ? calculatedValueFromModal 
      : value;
    
    setLoading(true);
    try {
      if (isNewEntry) {
        // For new entries, just update local state (will be saved when OK is clicked)
        setMatchingReports(prev => prev.map(r => 
          r.id === targetId 
            ? { 
                ...r, 
                key: key,
                isInternalKey: isInternalKey,
                internalKey: internalKey,
                value: finalValue,
                isAmountPercentage: isAmountPercentage,
                percentage: percentageValue,
                isNew: true
              }
            : r
        ));
        toast.success(t('EntryUpdatedSuccessfully') || 'Entry updated successfully.');
      } else {
        // For existing entries, save immediately to database
        const existingReport = matchingReports.find(r => r.id === targetId);
        const reportToSave = {
          id: targetId,
          caseId: client.caseId,
          key: key,
          isInternalKey: isInternalKey,
          internalKey: (isInternalKey && internalKey) ? internalKey : null, // Only set if IsInternalKey is true and internalKey exists
          type: existingReport?.type || 0,
          value: finalValue,
          isAmountPercentage: isAmountPercentage,
          percentage: percentageValue, // Use percentage from modal or existing
          year: year,
          isFromProfitAndLossDetailed: existingReport?.isFromProfitAndLossDetailed ?? isFromLedgerReport
        };

        const savedReports = await ProfitAndLossService.SaveMatchingProfitAndLossReports([reportToSave], token);
        
        if (savedReports && savedReports.length > 0) {
          // Update local state with saved report
          setMatchingReports(prev => {
            const updated = prev.map(r => 
              r.id === targetId 
                ? { ...savedReports[0], isNew: false, isModified: false }
                : r
            );
            
            // Recalculate and save corporate tax if needed
            updateCorporateTaxAfterChange(updated);
            
            return updated;
          });
          
          toast.success(t('EntryUpdatedSuccessfully') || 'Entry updated successfully.');
        }
      }
      // Close modal after successful update
      handleCloseEditModal();
    } catch (error) {
      console.error('Error updating entry:', error);
      toast.error(t('ErrorUpdatingEntry') || `Error updating entry: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    // Show confirmation dialog
    const result = await Swal.fire({
      title: t('ConfirmDelete') || 'Confirm Delete',
      text: t('AreYouSureDeleteEntry') || 'Are you sure you want to delete this entry? This action cannot be undone.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#6c757d',
      confirmButtonText: t('YesDelete') || 'Yes, Delete',
      cancelButtonText: t('Cancel') || 'Cancel',
      reverseButtons: true
    });

    if (!result.isConfirmed) {
      return;
    }

    // If it's a temporary ID (new entry), just remove from local state
    if (id.toString().startsWith('temp_')) {
      setMatchingReports(prev => {
        const updated = prev.filter(r => r.id !== id);
        
        // Recalculate and save corporate tax if needed
        updateCorporateTaxAfterChange(updated);
        
        return updated;
      });
      toast.success(t('EntryDeletedSuccessfully') || 'Entry deleted successfully.');
      return;
    }

    // Otherwise, delete from server
    setLoading(true);
    try {
      await ProfitAndLossService.DeleteMatchingProfitAndLossReport(id, token);
      
      // Remove from local state and update corporate tax
      setMatchingReports(prev => {
        const updated = prev.filter(r => r.id !== id);
        
        // Recalculate and save corporate tax if needed
        updateCorporateTaxAfterChange(updated);
        
        return updated;
      });
      toast.success(t('EntryDeletedSuccessfully') || 'Entry deleted successfully.');
    } catch (error) {
      console.error('Error deleting matching report:', error);
      toast.error(t('ErrorDeletingEntry') || `Error deleting entry: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPdf = async () => {
    setLoading(true);
    try {
      // Filter by isFromProfitAndLossDetailed:
      // - If opened from LedgerReport (Profit and Loss Detailed), filter by isFromProfitAndLossDetailed = true
      // - If opened from regular ProfitAndLossReport, filter by isFromProfitAndLossDetailed = false
      const isFromProfitAndLossDetailed = isFromLedgerReport ? true : false;
      // When from LedgerReport, pass profitAndLossTotalBalance to use as totalProfit
      const profitAndLossTotalBalanceToPass = isFromLedgerReport ? profitAndLossTotalBalance : null;
      await ProfitAndLossService.DownloadMatchingProfitAndLossReportPdf(
        client.caseId,
        client.accountantId,
        dayjs.utc(`${year}-01-01`).toDate(),
        dayjs.utc(`${year}-12-31`).toDate(),
        year,
        token,
        isFromProfitAndLossDetailed,
        profitAndLossTotalBalanceToPass
      );
      toast.success(t('PdfDownloadedSuccessfully') || 'PDF downloaded successfully.');
    } catch (error) {
      console.error('Error downloading PDF:', error);
      toast.error(t('ErrorDownloadingPdf') || `Error downloading PDF: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Save all new entries and updates when OK is clicked
  const saveAllNewEntries = async () => {
    // Filter entries with temporary IDs (new entries not yet saved)
    const newEntries = matchingReports.filter(r => r.id.toString().startsWith('temp_'));
    // Filter entries that were modified (have isNew flag or were edited)
    // Also include corporate tax if it exists and value might have changed
    const corporateTaxEntry = matchingReports.find(r => r.key === 'בגין מס חברות' && r.type === 2);
    const updatedEntries = matchingReports.filter(r => {
      if (r.id.toString().startsWith('temp_')) {
        return false; // Exclude temp entries (they're in newEntries)
      }
      // Include if marked as new or modified
      if (r.isNew || r.isModified) {
        return true;
      }
      // Special case: Always include corporate tax if it exists and calculated value differs
      if (r.key === 'בגין מס חברות' && r.type === 2 && corporateTaxEntry) {
        const storedValue = parseFloat(r.value) || 0;
        const calculatedValue = calculatedTaxAmount;
        // If values differ significantly, include it for update
        if (Math.abs(storedValue - calculatedValue) > 0.01) {
          return true;
        }
      }
      return false;
    });
    
    // Check if deferred tax expenses needs to be saved
    const deferredTaxEntry = matchingReports.find(r => r.key === 'הוצאות מס נדחות' && r.type === 2);
    const existingDeferredTaxFromDB = matchingReports.find(r => 
      r.key === 'הוצאות מס נדחות' && r.type === 2 && !r.id.toString().startsWith('temp_')
    );
    const originalDeferredTaxValue = existingDeferredTaxFromDB ? parseFloat(existingDeferredTaxFromDB.value) || 0 : 0;
    
    const hasDeferredTaxChanges = deferredTaxEntry ? (
      deferredTaxEntry.id.toString().startsWith('temp_') || 
      deferredTaxEntry.isNew || 
      deferredTaxEntry.isModified ||
      parseFloat(deferredTaxEntry.value) !== originalDeferredTaxValue
    ) : (deferredTaxExpenses !== originalDeferredTaxValue);
    
    if (newEntries.length === 0 && updatedEntries.length === 0 && !hasDeferredTaxChanges) {
      toast.info(t('NoChangesToSave') || 'No changes to save.');
      return;
    }

    setLoading(true);
    try {
      // Prepare all reports for batch save (new entries and updates)
      // Also include deferred tax expenses if it exists
      const deferredTaxEntry = matchingReports.find(r => r.key === 'הוצאות מס נדחות' && r.type === 2);
      const entriesToSave = [
        ...newEntries.map(entry => {
          // For corporate tax, use the recalculated value to ensure correct value is saved
          let valueToSave = entry.value;
          if (entry.key === 'בגין מס חברות' && entry.type === 2 && calculatedTaxAmount > 0) {
            // Use the current calculated tax amount to ensure correct value
            valueToSave = calculatedTaxAmount.toFixed(2);
          }
          
          return {
            id: '00000000-0000-0000-0000-000000000000', // Empty GUID for new entries
            caseId: entry.caseId,
            key: entry.key,
            isInternalKey: entry.isInternalKey ?? false,
            internalKey: (entry.isInternalKey && entry.internalKey) ? entry.internalKey : null, // Only set if IsInternalKey is true and internalKey exists
            type: entry.type,
            value: valueToSave,
            isAmountPercentage: entry.isAmountPercentage,
            percentage: entry.percentage ?? null, // Include percentage if set
            year: entry.year,
            isFromProfitAndLossDetailed: entry.isFromProfitAndLossDetailed ?? isFromLedgerReport
          };
        }),
        ...updatedEntries.map(entry => {
          // For corporate tax, use the recalculated value to ensure correct value is saved
          let valueToSave = entry.value;
          if (entry.key === 'בגין מס חברות' && entry.type === 2 && calculatedTaxAmount > 0) {
            // Use the current calculated tax amount to ensure correct value
            valueToSave = calculatedTaxAmount.toFixed(2);
          }
          
          return {
            id: entry.id, // Include ID for updates
            percentage: entry.percentage ?? null, // Include percentage if set
            caseId: entry.caseId,
            key: entry.key,
            isInternalKey: entry.isInternalKey ?? false,
            internalKey: (entry.isInternalKey && entry.internalKey) ? entry.internalKey : null, // Only set if IsInternalKey is true and internalKey exists
            type: entry.type,
            value: valueToSave,
            isAmountPercentage: entry.isAmountPercentage,
            year: entry.year,
            isFromProfitAndLossDetailed: entry.isFromProfitAndLossDetailed ?? isFromLedgerReport
          };
        })
      ];
      
      // Add deferred tax expenses entry if it exists or needs to be updated
      if (deferredTaxEntry) {
        const isAlreadyIncluded = entriesToSave.some(e => 
          (e.id === deferredTaxEntry.id && !e.id.toString().startsWith('00000000')) ||
          (e.key === 'הוצאות מס נדחות' && e.type === 2)
        );
        if (!isAlreadyIncluded) {
          entriesToSave.push({
            id: deferredTaxEntry.id.toString().startsWith('temp_') 
              ? '00000000-0000-0000-0000-000000000000' 
              : deferredTaxEntry.id,
            caseId: deferredTaxEntry.caseId,
            key: deferredTaxEntry.key,
            isInternalKey: deferredTaxEntry.isInternalKey ?? false,
            internalKey: (deferredTaxEntry.isInternalKey && deferredTaxEntry.internalKey) ? deferredTaxEntry.internalKey : null,
            type: deferredTaxEntry.type,
            value: deferredTaxEntry.value,
            isAmountPercentage: deferredTaxEntry.isAmountPercentage,
            percentage: deferredTaxEntry.percentage ?? null,
            year: deferredTaxEntry.year,
            isFromProfitAndLossDetailed: deferredTaxEntry.isFromProfitAndLossDetailed ?? isFromLedgerReport
          });
        }
      } else if (existingDeferredTaxFromDB && deferredTaxExpenses === 0) {
        // If there's an existing entry in DB but user set it to 0, we need to update it
        entriesToSave.push({
          id: existingDeferredTaxFromDB.id,
          caseId: existingDeferredTaxFromDB.caseId,
          key: existingDeferredTaxFromDB.key,
          isInternalKey: existingDeferredTaxFromDB.isInternalKey ?? false,
          internalKey: (existingDeferredTaxFromDB.isInternalKey && existingDeferredTaxFromDB.internalKey) ? existingDeferredTaxFromDB.internalKey : null,
          type: existingDeferredTaxFromDB.type,
          value: '0',
          isAmountPercentage: true,
          percentage: existingDeferredTaxFromDB.percentage ?? null,
          year: existingDeferredTaxFromDB.year,
          isFromProfitAndLossDetailed: existingDeferredTaxFromDB.isFromProfitAndLossDetailed ?? isFromLedgerReport
        });
      } else if (deferredTaxExpenses > 0 && !deferredTaxEntry) {
        // If deferred tax expenses is set but no entry exists, create one
        entriesToSave.push({
          id: '00000000-0000-0000-0000-000000000000',
          caseId: client.caseId,
          key: 'הוצאות מס נדחות',
          isInternalKey: false,
          internalKey: null,
          type: 2, // TaxLiability
          value: deferredTaxExpenses.toString(),
          isAmountPercentage: true,
          year: year,
          isFromProfitAndLossDetailed: isFromLedgerReport
        });
      }
      
      const allReportsToSave = entriesToSave;

      // Send all reports in a single batch request
      const savedReports = await ProfitAndLossService.SaveMatchingProfitAndLossReports(allReportsToSave, token);
      
      // Replace temporary entries and updated entries with saved ones
      setMatchingReports(prev => {
        const updated = [...prev];
        const allEntriesToUpdate = [...newEntries, ...updatedEntries];
        
        // Also handle deferred tax entry if it was saved
        const deferredTaxInSaved = savedReports.find(r => r.key === 'הוצאות מס נדחות' && r.type === 2);
        if (deferredTaxInSaved) {
          const deferredTaxIndex = updated.findIndex(r => r.key === 'הוצאות מס נדחות' && r.type === 2);
          if (deferredTaxIndex !== -1) {
            updated[deferredTaxIndex] = { ...deferredTaxInSaved, isNew: false, isModified: false };
          } else {
            updated.push({ ...deferredTaxInSaved, isNew: false, isModified: false });
          }
        }
        
        allEntriesToUpdate.forEach((entry, index) => {
          const savedReport = savedReports.find(r => 
            (r.id === entry.id && !entry.id.toString().startsWith('temp_')) ||
            (entry.id.toString().startsWith('temp_') && r.key === entry.key && r.type === entry.type)
          );
          const entryIndex = updated.findIndex(r => r.id === entry.id);
          if (entryIndex !== -1 && savedReport) {
            updated[entryIndex] = { ...savedReport, isNew: false, isModified: false };
          } else if (savedReport && entryIndex === -1) {
            // Entry was saved but not found in updated array, add it
            updated.push({ ...savedReport, isNew: false, isModified: false });
          }
        });
        return updated;
      });
      
      // Reload all reports from server to ensure consistency
      await loadMatchingReports();
      
      // Show success toast
      toast.success(t('MatchingReportsSavedSuccessfully') || 'Matching reports saved successfully');
    } catch (error) {
      console.error('Error saving matching reports:', error);
      toast.error(`Error saving reports: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const getTypeDisplay = (type) => {
    return type === 0 ? t('Add') || 'הוסף' : t('Subtract') || 'הפחת';
  };

  const getValueTypeDisplay = (isAmount) => {
    return isAmount ? (t('Amount') || 'סכום') : (t('Percentage') || 'אחוז');
  };

  // Helper function to render inline form in summary
  const renderInlineForm = (summaryType, numericType) => {
    if (addingInSummary !== summaryType) return null;
    
    // Determine if we should use AccountCard or SortCode based on isFromLedgerReport
    const useAccountCard = isFromLedgerReport;
    const keyTypeForSelection = useAccountCard ? 'accountcard' : 'sortcode';
    const selectedKey = useAccountCard ? selectedAccountCard : selectedSortCode;
    const options = useAccountCard ? accountCardOptions : sortCodeOptions;
    
    const shouldShowTwoInputs = !isAmountPercentage && keyType === keyTypeForSelection && selectedKey;
    const isValid = (() => {
      if (isAmountPercentage) {
        return value && (keyType === keyTypeForSelection ? selectedKey : manualKey);
      } else {
        return inlineFormPercentage && inlineFormCalculatedValue && (keyType === keyTypeForSelection ? selectedKey : manualKey);
      }
    })();

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 8, padding: '8px', border: '1px solid #304FFF', borderRadius: 4, backgroundColor: '#f5f5f5' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          {/* Key Type Selector */}
          <FormControl size="small" style={{ minWidth: 120 }}>
            <InputLabel>{t('KeyType') || 'סוג מפתח'}</InputLabel>
            <Select
              value={keyType || (isFromLedgerReport ? 'accountcard' : 'sortcode')}
              onChange={(e) => {
                const newKeyType = e.target.value;
                setKeyType(newKeyType);
                setSelectedSortCode('');
                setSelectedAccountCard('');
                setManualKey('');
                setInlineFormSortCodeTotal(0);
                setInlineFormPercentage('');
                setInlineFormCalculatedValue('');
                // When switching to manual key type, set to amount mode (percentage doesn't apply)
                if (newKeyType === 'manual') {
                  setIsAmountPercentage(true);
                }
              }}
              label={t('KeyType') || 'סוג מפתח'}
              MenuProps={{
                disablePortal: false,
                style: { zIndex: 13000 },
                PaperProps: {
                  style: { zIndex: 13000 },
                },
              }}
            >
              {isFromLedgerReport ? (
                <MenuItem value="accountcard">{t('AccountCard') || 'כרטיס חשבון'}</MenuItem>
              ) : (
                <MenuItem value="sortcode">{t('SortCode') || 'קוד מיון'}</MenuItem>
              )}
              <MenuItem value="manual">{t('ManualText') || 'טקסט ידני'}</MenuItem>
            </Select>
          </FormControl>

          {/* Key Input */}
          <div style={{ flex: 1 }}>
            {(keyType === 'accountcard' && isFromLedgerReport) || (keyType === 'sortcode' && !isFromLedgerReport) ? (
              <Autocomplete
                options={options || []}
                getOptionLabel={(option) => useAccountCard 
                  ? `${option.cardId} - ${option.name}` 
                  : `${option.sortCodeId} - ${option.sortCodeName}`}
                value={useAccountCard
                  ? (accountCardOptions || []).find(opt => opt.cardId === selectedAccountCard) || null
                  : (sortCodeOptions || []).find(opt => opt.sortCodeId === selectedSortCode) || null}
                onChange={(event, newValue) => {
                  const newKey = useAccountCard ? (newValue ? newValue.cardId : '') : (newValue ? newValue.sortCodeId : '');
                  const currentKey = useAccountCard ? selectedAccountCard : selectedSortCode;
                  
                  // Only reset if the key actually changed (not just clearing)
                  if (newKey !== currentKey) {
                    if (useAccountCard) {
                      setSelectedAccountCard(newKey);
                    } else {
                      setSelectedSortCode(newKey);
                    }
                    // Reset percentage and calculated value when sort code/account card changes
                    setInlineFormPercentage('');
                    setInlineFormCalculatedValue('');
                    // Reset the total to 0 so it will be fetched fresh
                    setInlineFormSortCodeTotal(0);
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder={useAccountCard ? (t('AccountCard') || 'כרטיס חשבון') : (t('SortCode') || 'קוד מיון')}
                    variant="standard"
                    InputProps={{
                      ...params.InputProps,
                      ...inputPropsStyle,
                    }}
                    inputProps={{
                      ...params.inputProps,
                      style: { textAlign: 'right', direction: 'rtl' },
                    }}
                    size="small"
                  />
                )}
                renderOption={(props, option) => (
                  <Tooltip title={option.isEditable ? t('RightClickToEdit') || 'Right click to edit' : t('NotEditable') || 'Not editable'} placement="top">
                    <StyledListItem {...props}>
                      {useAccountCard 
                        ? `${option.cardId} - ${option.name}` 
                        : `${option.sortCodeId} - ${option.sortCodeName}`}
                    </StyledListItem>
                  </Tooltip>
                )}
                PopperComponent={(props) => (
                  <StyledPopper {...props} style={{ ...props.style, direction: 'rtl', zIndex: 13000 }} />
                )}
              />
            ) : (
              <TextField
                size="small"
                fullWidth
                placeholder={t('ManualKey') || 'מפתח ידני'}
                value={manualKey}
                onChange={(e) => setManualKey(e.target.value)}
              />
            )}
          </div>

          {/* Value Type Toggle - Only show for SortCode or AccountCard entries */}
          {(keyType === 'sortcode' || (isFromLedgerReport && keyType === 'accountcard')) && (
            <FormControlLabel
              control={
                <Switch
                  checked={isAmountPercentage}
                  onChange={(e) => {
                    const newMode = e.target.checked;
                    setIsAmountPercentage(newMode);
                    // Don't clear percentage when switching to amount mode - preserve it
                    // When switching to Amount mode, set value to the calculated value if available
                    if (newMode) {
                      // If we have a calculated value, use it; otherwise use the current value
                      if (inlineFormCalculatedValue) {
                        setValue(inlineFormCalculatedValue);
                      }
                      // If no calculated value but we have a sort code total, use that
                      else {
                        const useAccountCard = isFromLedgerReport && keyType === 'accountcard';
                        const selectedKey = useAccountCard ? selectedAccountCard : selectedSortCode;
                        if ((keyType === 'sortcode' || (isFromLedgerReport && keyType === 'accountcard')) && selectedKey && inlineFormSortCodeTotal !== 0) {
                          const totalStr = inlineFormSortCodeTotal.toFixed(2);
                          setValue(totalStr);
                        }
                      }
                      // Only clear calculated value, not percentage
                      setInlineFormCalculatedValue('');
                    }
                    // When switching back to percentage mode, restore calculated value if percentage exists
                    else {
                      const useAccountCard = isFromLedgerReport && keyType === 'accountcard';
                      const selectedKey = useAccountCard ? selectedAccountCard : selectedSortCode;
                      
                      // If we have a percentage, recalculate the value from it
                      if (inlineFormPercentage) {
                        const percentageNum = parseFloat(inlineFormPercentage);
                        if (!isNaN(percentageNum) && percentageNum > 0 && inlineFormSortCodeTotal !== 0) {
                          const calculated = (percentageNum / 100) * inlineFormSortCodeTotal;
                          const calculatedStr = calculated.toFixed(2);
                          setInlineFormCalculatedValue(calculatedStr);
                          setValue(calculatedStr);
                        }
                      }
                      // If no percentage but we have current value and total, calculate percentage from current value
                      else if (value && (keyType === 'sortcode' || (isFromLedgerReport && keyType === 'accountcard')) && selectedKey && inlineFormSortCodeTotal !== 0) {
                        const valueNum = parseFloat(value);
                        // Allow both positive and negative values for calculation
                        if (!isNaN(valueNum) && valueNum !== 0) {
                          const calculatedPercentage = (valueNum / inlineFormSortCodeTotal) * 100;
                          setInlineFormPercentage(calculatedPercentage.toFixed(2));
                          setInlineFormCalculatedValue(value);
                        }
                      }
                      // If no value but we have total, set calculated value to total
                      else if ((keyType === 'sortcode' || (isFromLedgerReport && keyType === 'accountcard')) && selectedKey && inlineFormSortCodeTotal !== 0) {
                        const totalStr = inlineFormSortCodeTotal.toFixed(2);
                        setInlineFormCalculatedValue(totalStr);
                        setValue(totalStr);
                      }
                      // Otherwise, just set calculated value to current value if it exists
                      else if (value) {
                        setInlineFormCalculatedValue(value);
                      }
                    }
                  }}
                  size="small"
                />
              }
              label={isAmountPercentage ? (t('Amount') || 'סכום') : (t('Percentage') || 'אחוז')}
              style={{ margin: 0, whiteSpace: 'nowrap' }}
            />
          )}

          {/* Value Inputs */}
          {shouldShowTwoInputs ? (
            // Percentage mode with SortCode: Show two inputs (percentage and calculated value)
            <>
              <div style={{ width: '60px', flexShrink: 0 }}>
                <TextField
                  size="small"
                  fullWidth
                  type="number"
                  placeholder={t('Percentage') || 'אחוז'}
                  value={inlineFormPercentage}
                  onChange={(e) => {
                    const newPercentage = e.target.value;
                    // Allow empty string for clearing
                    if (newPercentage === '') {
                      setInlineFormPercentage('');
                      return;
                    }
                    // Validate percentage range
                    const percentageNum = parseFloat(newPercentage);
                    if (!isNaN(percentageNum)) {
                      if (percentageNum < 0) {
                        toast.error(t('PercentageMustBeGreaterThanZero') || 'האחוז חייב להיות גדול או שווה ל-0');
                        setInlineFormPercentage('');
                        return;
                      }
                      if (percentageNum > 100) {
                        toast.error(t('PercentageMustBeLessThanOrEqualToOneHundred') || 'האחוז חייב להיות קטן או שווה ל-100');
                        setInlineFormPercentage('');
                        return;
                      }
                    }
                    setInlineFormPercentage(newPercentage);
                  }}
                />
              </div>
              <div style={{ flex: 1, marginLeft: '8px' }}>
                <TextField
                  size="small"
                  fullWidth
                  type="number"
                  placeholder={t('CalculatedValue') || 'ערך מחושב'}
                  value={inlineFormCalculatedValue}
                  disabled
                  InputProps={{
                    readOnly: true,
                  }}
                />
              </div>
            </>
          ) : (
            // Amount mode or manual key: Show single input
            <div style={{ flex: 1 }}>
              <TextField
                size="small"
                fullWidth
                type="number"
                placeholder={isAmountPercentage ? (t('Amount') || 'סכום') : (t('Percentage') || 'אחוז')}
                value={value}
                onChange={(e) => setValue(e.target.value)}
              />
            </div>
          )}

          {/* Add Button */}
          <IconButton
            size="small"
            onClick={async () => {
              // Pass percentage if in percentage mode with SortCode or AccountCard
              const percentageToPass = shouldShowTwoInputs && inlineFormPercentage ? parseFloat(inlineFormPercentage) : null;
              
              // Add entry and save immediately to database
              await handleAddEntry(numericType, percentageToPass, true);
            }}
            disabled={loading || !isValid}
            sx={{ color: '#304FFF' }}
          >
            <AddIcon />
          </IconButton>

          {/* Cancel Button */}
          <IconButton
            size="small"
            onClick={handleCancelAdd}
            sx={{ color: 'red' }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </div>
      </div>
    );
  };

  // Calculate current tax expenses (הוצאות מס שוטפות) - sum of חבות מס items only (excluding deferred tax)
  const currentTaxExpenses = taxLiabilityItems.reduce((sum, item) => sum + (parseFloat(item.value) || 0), 0);
  
  // Calculate total tax expenses (סה״כ הוצאות מס) - current + deferred
  const totalTaxExpenses = currentTaxExpenses + deferredTaxExpenses;

  if (!open) return null;

  return (
    <div style={{
      position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,.67)', zIndex: 3000,
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 16
    }}
    >
      <div style={{
        background: '#FFF', width: '95%', maxWidth: '1300px', height: '90%', borderRadius: 8, padding: 16,
        display: 'flex', flexDirection: 'column', boxShadow: '0 4px 10px rgba(0,0,0,.25)'
      }}
      >
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 8
        }}>
        <IconButton
            size="small"
            onClick={onCancel}
            sx={{
              color: '#666',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
                color: '#000'
              }
            }}
          >
            <CloseIcon />
          </IconButton>
          <div style={{
            fontSize: '18px',
            fontWeight: 600,
            color: '#304FFF',
            flex: 1,
            textAlign: 'center'
          }}>
            {t(title)}
          </div>
        </div>

        <div style={{
          flex: 1,
          display: 'flex',
          gap: 16,
          overflow: 'hidden',
          minHeight: 0,
          minWidth: 0,
        }}>
          {/* Left Side - Form */}
          <MatchingReportForm
            // Data
            addItems={addItems}
            subtractItems={subtractItems}
            taxLiabilityItems={taxLiabilityItems}
            totalProfit={isFromLedgerReport ? profitAndLossTotalBalance : totalProfit}
            taxableIncomeBase={taxableIncomeBase}
            taxableIncomeRateItem={taxableIncomeRateItem}
            taxableIncomePercentage={taxableIncomePercentage}
            taxPercentage={taxPercentage}
            currentTaxExpenses={currentTaxExpenses}
            deferredTaxExpenses={deferredTaxExpenses}
            totalTaxExpenses={totalTaxExpenses}
            matchingReports={matchingReports}
            sortCodeOptions={sortCodeOptions}
            year={normalizedYear}
            client={client}
            loading={loading}
            
            // State
            addingToAdd={addingToAdd}
            addingToSubtract={addingToSubtract}
            addingInSummary={addingInSummary}
            
            // Form state
            keyType={keyType}
            selectedSortCode={selectedSortCode}
            manualKey={manualKey}
            value={value}
            isAmountPercentage={isAmountPercentage}
            
            // Handlers
            setAddingInSummary={setAddingInSummary}
            setAddingToAdd={setAddingToAdd}
            setAddingToSubtract={setAddingToSubtract}
            setKeyType={setKeyType}
            setSelectedSortCode={setSelectedSortCode}
            setManualKey={setManualKey}
            setValue={setValue}
            setIsAmountPercentage={setIsAmountPercentage}
            handleAddEntry={handleAddEntry}
            handleCancelAdd={handleCancelAdd}
            handleEdit={handleEdit}
            handleDelete={handleDelete}
            handleDownloadPdf={handleDownloadPdf}
            getKeyDisplay={getKeyDisplay}
            renderInlineForm={renderInlineForm}
            setTaxableIncomePercentage={setTaxableIncomePercentage}
            setDeferredTaxExpenses={setDeferredTaxExpenses}
            setMatchingReports={setMatchingReports}
            handleSaveTaxableIncomePercentage={handleSaveTaxableIncomePercentage}
            
            // Styles
            inputPropsStyle={inputPropsStyle}
            
            // Translation
            t={t}
            
            // Percentage calculation
            onGetSortCodeTotal={getSortCodeTotal}
            token={token}
          />

          {/* Right Side - HTML Preview */}
          <div style={{
            flex: '1 1 45%',
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
            border: '1px solid #E0E0E0',
            borderRadius: 8,
            padding: 8,
            overflow: 'hidden',
            position: 'relative',
          }}>
            {htmlUrl ? (
              <iframe
                src={htmlUrl}
                title={t(title)}
                scrolling="auto"
                style={{
                  width: '100%',
                  height: '100%',
                  border: 'none',
                  flex: 1,
                  minHeight: 0,
                  display: 'block',
                }}
              />
            ) : (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                color: '#666'
              }}>
                {t('Loading')}...
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Action buttons */}
      <div style={{ marginTop: 12, display: 'flex', gap: 16 }}>
        {withCancel && (
          <Button variant="contained" sx={{ bgcolor: 'red', width: '100px' }} onClick={onCancel}>
             {t('Cancel')}
          </Button>
        )}
        <Button 
          variant="contained" 
          sx={{ bgcolor: '#304FFF', width: '100px' }} 
          onClick={async () => {
            await saveAllNewEntries();

          }}
          disabled={loading}
        >
          {t('Save')}
        </Button>
      </div>

      {/* Edit Entry Modal */}
      <EditEntryModal
        open={editModalOpen}
        onClose={handleCloseEditModal}
        onSave={(percentageFromModal, calculatedValueFromModal) => handleUpdateEntry(null, percentageFromModal, calculatedValueFromModal)}
        loading={loading}
        t={t}
        keyType={keyType}
        setKeyType={setKeyType}
        selectedSortCode={selectedSortCode}
        setSelectedSortCode={setSelectedSortCode}
        selectedAccountCard={selectedAccountCard}
        setSelectedAccountCard={setSelectedAccountCard}
        manualKey={manualKey}
        setManualKey={setManualKey}
        value={value}
        setValue={setValue}
        isAmountPercentage={isAmountPercentage}
        setIsAmountPercentage={setIsAmountPercentage}
        sortCodeOptions={sortCodeOptions}
        accountCardOptions={accountCardOptions}
        inputPropsStyle={inputPropsStyle}
        onGetSortCodeTotal={getSortCodeTotal}
        onGetAccountCardTotal={getAccountCardTotal}
        client={client}
        year={year}
        token={token}
        editingItem={editingItem}
        isFromLedgerReport={isFromLedgerReport}
      />
    </div>
  );
};

export default ProfitAndLossHtmlPreview;